import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

export default function LocationUpdateDialog({ tripId, onClose, onSaved }) {
  const [locationRef, setLocationRef] = useState("");
  const [remarks, setRemarks] = useState("");
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!locationRef.trim()) return toast.error("Location Reference is required");
    setSaving(true);
    const fd = new FormData();
    fd.append("location_reference", locationRef.trim());
    if (remarks.trim()) fd.append("remarks", remarks.trim());
    if (photo) fd.append("photo", photo);
    try {
      await client.post(`/transport-log/trips/${tripId}/location-updates`, fd);
      toast.success("Location update added");
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="location-update-dialog">
        <DialogHeader>
          <DialogTitle>Add Location Update</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Location Reference *</label>
            <Input
              data-testid="location-form-reference-input"
              value={locationRef}
              onChange={(e) => setLocationRef(e.target.value)}
              placeholder="Address / Plus Code / Lat,Long"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Remarks</label>
            <Textarea data-testid="location-form-remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">Photo</p>
            {photo ? (
              <div className="relative h-16 w-16">
                <img src={URL.createObjectURL(photo)} alt="preview" className="h-16 w-16 rounded object-cover border border-slate-200" />
                <button
                  type="button"
                  data-testid="location-form-photo-remove"
                  onClick={() => setPhoto(null)}
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-slate-900 text-white flex items-center justify-center"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <label className="h-16 w-16 rounded border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 hover:border-amber-500 hover:text-amber-600 transition-colors cursor-pointer">
                <Upload className="h-5 w-5" />
                <input type="file" accept="image/*" hidden data-testid="location-form-photo-input" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
              </label>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="location-form-cancel-button">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="location-form-submit-button" className="bg-amber-600 hover:bg-amber-700">
              {saving ? "Saving..." : "Add Update"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
